// =============================================================================
// lib/icons.ts — normalize community/user icon URLs for React Native <Image>.
//
// Many community/user icons are dicebear SVGs, e.g.
//   https://api.dicebear.com/7.x/shapes/svg?seed=Foo
// RN's <Image> cannot rasterize SVG, so those render as blank/gray circles.
// Dicebear serves the SAME avatar as PNG when you swap the `/svg` path segment
// for `/png`, which RN CAN render. We rewrite dicebear SVG URLs to PNG and
// return every other URL unchanged.
// =============================================================================

/**
 * Returns an <Image>-safe icon URL.
 * - dicebear SVG endpoints ("/svg") are rewritten to their PNG equivalent.
 * - all other URLs are returned unchanged.
 * - undefined/empty input returns undefined (so callers keep their fallback).
 */
export function displayableIcon(url?: string): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;

  // dicebear: turn the "/svg" path segment into "/png" (query string preserved).
  if (/(^|\.)dicebear\.com\//i.test(trimmed)) {
    return trimmed.replace(/\/svg(\?|$)/i, '/png$1');
  }

  return trimmed;
}

// =============================================================================
// Community icons — real crypto logos instead of generated dicebear blobs.
//
// Most CRYB communities are coin communities (Bitcoin, Ethereum, Solana, …), so
// when a community's name/displayName maps to a known ticker we serve the real,
// crisp coin logo from a pinned public CDN (atomiclabs/cryptocurrency-icons via
// jsDelivr — color PNGs RN <Image> renders fine). Anything we can't map falls
// back to the (now PNG-safe) stored icon so it still shows something.
// =============================================================================

// Pinned commit so the asset URLs never drift.
const COIN_ICON_BASE =
  'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color';

// The tickers the CDN actually ships (subset covering the majors + popular alts).
const KNOWN_SYMBOLS = new Set([
  'btc','eth','usdt','usdc','bnb','xrp','sol','ada','doge','trx','dot','matic',
  'ltc','shib','avax','link','xlm','atom','xmr','etc','bch','uni','fil','xtz',
  'algo','vet','near','icp','egld','aave','eos','xdc','miota','mkr','grt','snx',
  'crv','comp','ftt','dash','zec','waves','bat','enj','chz','sushi','yfi','1inch',
  'omg','zil','qtum','ont','dcr','rvn','kava','ankr','storj','bnt','celo','rsr',
  'sc','btg','lsk','nano','xno','hbar','theta','tfuel','hot','icx','zrx','ren',
  'knc','lrc','cvc','gno','rlc','pax','tusd','busd','gusd','husd','one',
]);

// Full-name / alias → CDN ticker. Keys are normalized (lowercase, alnum only).
const NAME_TO_SYMBOL: Record<string, string> = {
  bitcoin: 'btc', ethereum: 'eth', tether: 'usdt', usdcoin: 'usdc', usdollarcoin: 'usdc',
  binance: 'bnb', binancecoin: 'bnb', bnbchain: 'bnb', ripple: 'xrp', solana: 'sol',
  cardano: 'ada', dogecoin: 'doge', tron: 'trx', polkadot: 'dot',
  polygon: 'matic', litecoin: 'ltc', shibainu: 'shib', shiba: 'shib',
  avalanche: 'avax', chainlink: 'link', stellar: 'xlm', cosmos: 'atom', monero: 'xmr',
  ethereumclassic: 'etc', bitcoincash: 'bch', uniswap: 'uni', filecoin: 'fil',
  tezos: 'xtz', algorand: 'algo', vechain: 'vet', nearprotocol: 'near',
  internetcomputer: 'icp', elrond: 'egld', multiversx: 'egld', aave: 'aave', eos: 'eos',
  maker: 'mkr', thegraph: 'grt', synthetix: 'snx', curve: 'crv', curvedao: 'crv',
  compound: 'comp', dash: 'dash', zcash: 'zec', waves: 'waves', basicattentiontoken: 'bat',
  enjin: 'enj', enjincoin: 'enj', chiliz: 'chz', sushiswap: 'sushi', sushi: 'sushi',
  yearn: 'yfi', yearnfinance: 'yfi', hedera: 'hbar', theta: 'theta',
  loopring: 'lrc', decred: 'dcr', ravencoin: 'rvn', kava: 'kava', ankr: 'ankr',
  celo: 'celo', bitcoingold: 'btg', nano: 'xno', qtum: 'qtum', ontology: 'ont',
  zilliqa: 'zil', kyber: 'knc', kybernetwork: 'knc', ren: 'ren', zerox: 'zrx',
  gnosis: 'gno', iota: 'miota', harmony: 'one',
};

const normalizeToken = (s?: string): string =>
  (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// -----------------------------------------------------------------------------
// Real photos for topic communities (no coin ticker, no custom upload) — beats
// the generated dicebear blobs. Curated, verified Pexels images (hotlinkable,
// RN <Image>-safe), themed by the community's topic and picked deterministically
// so a community's photo is stable.
// -----------------------------------------------------------------------------
const pexels = (id: number, w = 400): string =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}&h=${w}&fit=crop`;

const THEME_POOLS: Record<string, number[]> = {
  crypto: [730547, 843700, 8369648],
  trading: [6770610, 7567443, 5980856, 6772076, 5980743],
  finance: [259027, 373543],
  nft: [1097930, 2748757, 3695238],
  tech: [1181671, 844124, 159888],
  general: [14751274, 730547, 6770610, 1181671, 2748757, 843700],
};

const themeFor = (s: string): string => {
  const t = normalizeToken(s);
  if (/nft|art|gallery|collect|pfp|paint/.test(t)) return 'nft';
  if (/defi|yield|stake|liquid|finance|fund|dao|treasury/.test(t)) return 'finance';
  if (/trad|chart|pit|degen|maxi|bull|bear|alpha|signal|pump|moon/.test(t)) return 'trading';
  if (/dev|code|build|tech|protocol|chain|node|infra/.test(t)) return 'tech';
  if (/btc|bitcoin|eth|coin|crypto|token|web3/.test(t)) return 'crypto';
  return 'general';
};

/**
 * A wide COVER image for a community card. Prefers a real custom upload; else a
 * real topic-themed photo. (Never a coin logo — logos look wrong stretched full
 * width — and never a dicebear blob.)
 */
export function communityCover(nameLike?: string, displayName?: string, iconUrl?: string, w = 600): string {
  const stored = displayableIcon(iconUrl);
  if (stored && !/dicebear\.com/i.test(stored)) return stored;
  return communityPhoto(nameLike, displayName, w);
}

/**
 * A wide COVER image for a LEARN ARTICLE (#44). Prefers the article's real
 * imageUrl/image; else a stable, topic-themed photo keyed off its title/slug so
 * every lesson card + hero shows a real featured image (never the flat purple
 * gradient) even before an admin uploads one. SVG-safe.
 */
export function learnCover(title?: string, slug?: string, imageUrl?: string, w = 800): string {
  const stored = displayableIcon(imageUrl);
  if (stored && !/dicebear\.com/i.test(stored)) return stored;
  return communityPhoto(title, slug, w);
}

/** A stable, real, topic-themed photo for a community that has no logo/upload. */
export function communityPhoto(nameLike?: string, displayName?: string, w = 400): string {
  const key = normalizeToken(nameLike || displayName || 'cryb') || 'cryb';
  const pool = THEME_POOLS[themeFor(`${nameLike || ''} ${displayName || ''}`)] || THEME_POOLS.general;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return pexels(pool[h % pool.length], w);
}

/**
 * Best real logo for a community.
 * 1. a real CUSTOM upload (non-dicebear stored icon) always wins — never override
 *    what a community deliberately set.
 * 2. else name/displayName maps to a known crypto ticker → the coin's real CDN logo.
 * 3. else the stored icon, made <Image>-safe (dicebear SVG→PNG).
 * 4. else undefined (caller keeps its lettered fallback).
 */
export function communityIcon(nameLike?: string, displayName?: string, iconUrl?: string): string | undefined {
  const stored = displayableIcon(iconUrl);
  // A deliberate custom upload (anything that isn't a generated dicebear blob).
  if (stored && !/dicebear\.com/i.test(stored)) return stored;

  for (const candidate of [nameLike, displayName]) {
    const norm = normalizeToken(candidate);
    if (!norm) continue;
    if (KNOWN_SYMBOLS.has(norm)) return `${COIN_ICON_BASE}/${norm}.png`;
    const sym = NAME_TO_SYMBOL[norm];
    if (sym) return `${COIN_ICON_BASE}/${sym}.png`;
  }
  // No coin logo + no custom upload → a real topic-themed photo (never a blob).
  return communityPhoto(nameLike, displayName, 400);
}
